// Etapa 15: situação (selo) dos certificados A1 e aviso de vencimento.
// Funções puras: o painel só mostra o que elas devolvem.

/** Até quantos dias antes do vencimento o painel passa a avisar. */
export const DIAS_PARA_AVISO = 30;

export type SituacaoCertificado = 'valido' | 'vencendo' | 'vence_hoje' | 'vencido' | 'revogado';

/** Cor do selo: o componente traduz para as cores do tema. */
export type CorDoSelo = 'verde' | 'amarelo' | 'vermelho' | 'cinza';

/** Só o que a lógica precisa de cada certificado devolvido por GET /certificates. */
export interface CertificadoParaSelo {
  /** Dias até vencer (negativo = vencido; 0 = vence hoje), já calculado pelo servidor. */
  days_to_expire: number;
  active: boolean;
  label?: string;
}

export interface Selo {
  situacao: SituacaoCertificado;
  texto: string;
  cor: CorDoSelo;
}

function diasPorExtenso(dias: number): string {
  return `${dias} ${dias === 1 ? 'dia' : 'dias'}`;
}

/** Revogado > vencido > vence hoje > vence em N dias (≤ 30) > válido. */
export function seloDoCertificado(certificado: CertificadoParaSelo): Selo {
  if (!certificado.active) {
    return { situacao: 'revogado', texto: 'Removido do Lictor', cor: 'cinza' };
  }
  const dias = certificado.days_to_expire;
  if (dias < 0) {
    return { situacao: 'vencido', texto: 'Vencido', cor: 'vermelho' };
  }
  if (dias === 0) {
    return { situacao: 'vence_hoje', texto: 'Vence hoje', cor: 'amarelo' };
  }
  if (dias <= DIAS_PARA_AVISO) {
    return { situacao: 'vencendo', texto: `Vence em ${diasPorExtenso(dias)}`, cor: 'amarelo' };
  }
  return { situacao: 'valido', texto: 'Válido', cor: 'verde' };
}

/** "2027-03-05T00:00:00" → "05/03/2027". Corta o texto em vez de criar um Date (evita virar o dia por fuso). */
export function formatarValidade(iso: string): string {
  const [ano, mes, dia] = iso.slice(0, 10).split('-');
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : iso;
}

/** Certificados que merecem aviso: em uso (não revogados) e vencidos ou vencendo em até 30 dias. */
export function certificadosEmAlerta<T extends CertificadoParaSelo>(certificados: T[]): T[] {
  return certificados.filter((c) => c.active && c.days_to_expire <= DIAS_PARA_AVISO);
}

/** Texto da faixa do dashboard; null quando não há nada a avisar (a faixa não aparece). */
export function mensagemDoAviso(certificados: CertificadoParaSelo[]): string | null {
  const emAlerta = certificadosEmAlerta(certificados);
  if (emAlerta.length === 0) return null;
  if (emAlerta.length === 1) {
    const [unico] = emAlerta;
    const nome = unico.label ? `"${unico.label}" ` : '';
    const { situacao } = seloDoCertificado(unico);
    if (situacao === 'vencido') return `Atenção: o certificado ${nome}está vencido.`;
    if (situacao === 'vence_hoje') return `Atenção: o certificado ${nome}vence hoje.`;
    return `Atenção: o certificado ${nome}vence em ${diasPorExtenso(unico.days_to_expire)}.`;
  }
  return `Atenção: ${emAlerta.length} certificados estão vencidos ou perto de vencer.`;
}
